import React from 'react';
import { BlogPost } from '../types';
import { ArrowLeft, Calendar, Clock, User, Share2 } from 'lucide-react';

interface BlogPostProps {
  post: BlogPost;
  onBack: () => void;
}

const BlogPostView: React.FC<BlogPostProps> = ({ post, onBack }) => {

  // Helper to render content which might be string (from Admin) or JSX (from Seed Data)
  const renderContent = (content: React.ReactNode) => {
    if (typeof content === 'string') {
        // Split by double newline to create paragraphs
        return content.split('\n\n').map((paragraph, index) => (
            <p key={index} className="mb-6">{paragraph}</p>
        ));
    }
    return content;
  };

  return (
    <article className="bg-white min-h-screen pb-24">
      {/* Sticky Header */}
      <div className="sticky top-20 z-30 bg-white/90 backdrop-blur border-b border-gray-100 py-4 transition-all">
        <div className="max-w-3xl mx-auto px-4 flex justify-between items-center">
             <button 
                onClick={onBack}
                className="flex items-center text-gray-600 hover:text-orange-600 font-medium transition-colors text-sm md:text-base"
             >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
             </button>
             <button className="text-gray-400 hover:text-gray-900">
                <Share2 className="w-5 h-5" />
             </button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 pt-8 md:pt-12">
        {/* Article Header */}
        <div className="mb-8 md:mb-10 text-center">
            <div className="inline-flex items-center px-3 py-1 rounded-full bg-orange-50 text-orange-800 text-xs font-bold uppercase tracking-wide mb-4 md:mb-6">
                {post.category}
            </div>
            <h1 className="text-3xl md:text-5xl font-extrabold text-gray-900 mb-6 md:mb-8 leading-tight">
                {post.title}
            </h1>
            
            <div className="flex flex-wrap justify-center gap-4 md:gap-6 text-xs md:text-sm text-gray-500 border-y border-gray-100 py-4 md:py-6">
                <div className="flex items-center">
                    <User className="w-4 h-4 mr-2 text-orange-500" />
                    <span className="font-bold text-gray-900 mr-1">{post.author}</span>
                </div>
                <div className="flex items-center">
                    <Calendar className="w-4 h-4 mr-2 text-orange-500" />
                    {post.date}
                </div>
                <div className="flex items-center">
                    <Clock className="w-4 h-4 mr-2 text-orange-500" />
                    {post.readTime}
                </div>
            </div>
        </div>

        {/* Featured Image */}
        <div className="rounded-2xl overflow-hidden mb-8 md:mb-12 shadow-2xl aspect-video relative max-h-[500px]">
            <img 
                src={post.imageUrl} 
                alt={post.title} 
                loading="eager"
                fetchPriority="high"
                width={800}
                height={450}
                className="w-full h-full object-cover absolute inset-0"
            />
        </div>

        {/* Article Body */}
        <div className="prose prose-base md:prose-lg max-w-none prose-headings:font-bold prose-headings:text-gray-900 prose-p:text-gray-700 prose-a:text-orange-600 hover:prose-a:text-orange-500 prose-strong:text-gray-900 prose-li:text-gray-700">
            {renderContent(post.content)}
            
            <div className="mt-12 pt-8 border-t border-gray-100 not-prose">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Start Your Brand Journey</h3>
                <p className="text-gray-700 text-lg mb-6">
                    Ready to turn your vision into reality? BuildMySupplement offers end-to-end solutions for aspiring brand owners. 
                    Explore our <a href="/products" className="text-orange-600 font-bold hover:underline">Private Label Products</a> catalogue to see our ready-to-label formulas, 
                    learn more about our <a href="/resources" className="text-orange-600 font-bold hover:underline">Manufacturing Resources</a>, 
                    or <a href="/contact" className="text-orange-600 font-bold hover:underline">Contact our Experts</a> for a free customized consultation.
                </p>
            </div>
        </div>

        {/* Author Box */}
        <div className="mt-12 md:mt-16 p-6 md:p-8 bg-gray-50 rounded-2xl border border-gray-100 flex flex-col sm:flex-row items-center gap-4 md:gap-6 text-center sm:text-left">
            <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center text-2xl font-bold text-gray-400 uppercase flex-shrink-0">
                {post.author.charAt(0)}
            </div>
            <div>
                <h4 className="font-bold text-gray-900 text-lg">About {post.author}</h4>
                <p className="text-gray-600 text-sm mt-1">
                    Helping founders navigate the complex world of supplement manufacturing. {post.role} at BuildMySupplement.
                </p>
            </div>
        </div>
      </div>
    </article>
  );
};

export default BlogPostView;